import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { PageState } from "@/components/ui/PageState";
import { PRIVATE_CHART_TOOLTIP_CLASS } from "@/components/charts/privateTooltips";
import { getSessionBoundary, sessionOwnerKey } from "@/lib/api/session";
import { AccountController } from "../model/accountController";
import { AccountContext } from "../model/context";
import { AccountForm } from "./AccountForm";

export function AccountGate({ children }: { children: (identityKey: string) => ReactNode }) {
  const [controller] = useState(() => new AccountController());
  const state = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const privateRoot = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  useEffect(() => { controller.start(); return () => controller.stop(); }, [controller]);
  useEffect(() => {
    const hide = () => {
      if (controller.getSnapshot().status === "legacy") return;
      document.documentElement.setAttribute("data-account-suspended", "");
      if (privateRoot.current) privateRoot.current.hidden = true;
      controller.suspend();
    };
    const show = () => {
      if (controller.getSnapshot().status === "legacy") return;
      hide(); void controller.refresh();
    };
    const visibility = () => document.visibilityState === "visible" ? show() : hide();
    window.addEventListener("pagehide", hide);
    window.addEventListener("pageshow", show);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      window.removeEventListener("pagehide", hide); window.removeEventListener("pageshow", show);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [controller]);
  const ready = state.status === "ready" || state.status === "legacy";
  useLayoutEffect(() => {
    document.documentElement.toggleAttribute("data-account-suspended", !ready);
    return () => document.documentElement.removeAttribute("data-account-suspended");
  }, [ready]);
  const mounted = state.status === "legacy" || (state.session && !state.session.user.must_change_password);
  return <AccountContext.Provider value={{ controller, state }}>
    <style>{`[data-account-suspended] [data-account-private], [data-account-suspended] .${PRIVATE_CHART_TOOLTIP_CLASS} { display: none !important; }`}</style>
    {mounted && <div ref={privateRoot} hidden={!ready} data-account-private>{children(`${sessionOwnerKey()}:${getSessionBoundary().ownerGeneration}`)}</div>}
    {!ready && <main className="flex min-h-screen items-center justify-center bg-background p-4 text-foreground">
      {state.status === "login" || state.status === "password"
        ? <AccountForm key={state.status} password={state.status === "password"} />
        : <div className="w-full max-w-md"><PageState kind={state.status === "error" ? "error" : "loading"}
          title={t(state.status === "error" ? "account.checkError" : "account.checking")}
          description={state.status === "error" ? t("account.checkErrorHint") : undefined}
          onRetry={state.status === "error" ? () => void controller.refresh() : undefined} /></div>}
    </main>}
  </AccountContext.Provider>;
}
